import type { addBreadcrumb, captureException as sentryCaptureException } from '@sentry/angular';

export type TCaptureContext = Parameters<typeof sentryCaptureException>[1];

export interface ISentryApi {
    captureException: typeof sentryCaptureException;
    addBreadcrumb: typeof addBreadcrumb;
}

interface IQueuedCapture {
    error: unknown;
    context?: TCaptureContext;
}

const MAX_QUEUE_SIZE = 50;

let api: ISentryApi | null = null;
let loadTrigger: (() => void) | null = null;
const queue: IQueuedCapture[] = [];

/**
 * Drop-in replacement for `captureException` from `@sentry/angular` that keeps the SDK off the
 * critical path. Until the SDK is loaded the call is queued and the loader is nudged; afterwards it
 * is a straight pass-through.
 */
export function captureException(error: unknown, context?: TCaptureContext): void {
    if (api) {
        api.captureException(error, context);

        return;
    }

    if (queue.length < MAX_QUEUE_SIZE) {
        queue.push({ error, context });
    }

    loadTrigger?.();
}

/** Set by the browser scheduler so that the first captured error can pull the SDK in immediately. */
export function setSentryLoadTrigger(trigger: () => void): void {
    loadTrigger = trigger;
}

/** Called by the loader once the SDK is initialized. Flushes everything captured in the meantime. */
export function setSentryApi(sentryApi: ISentryApi): void {
    api = sentryApi;
    loadTrigger = null;

    queue.splice(0).forEach(({ error, context }) => sentryApi.captureException(error, context));
}

export function isSentryLoaded(): boolean {
    return api !== null;
}
